import React, { useState, ChangeEventHandler, useEffect } from "react";
import GlobalSearch from "../common/GlobalSearch";
import { ArrowDown, ArrowUp, Clear, FilterIcon } from "../common/ProjectIcons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SelectInput from "../common/SelectInput";
import { useTranslation } from "react-i18next";
import { DateRange, LogLevel, LogTypes } from "../common/ObjectTypes";

type LogsFilterProps = {
    filters: any[],
    globalSearchStr?: string,
    timezones: any[],
    selectedTimezone: any,
    onChangeGlobalSearch: Function,
    onRemoveTag: any,
    onResetFilters: any,
    onChangeStatusFilter: ChangeEventHandler,
    onChangeLogType: ChangeEventHandler,
    onChangeLogDate: Function,
    onSelectTimezone: Function
}

const LogsFilter = ({
    filters,
    globalSearchStr,
    timezones,
    selectedTimezone,
    onChangeGlobalSearch,
    onRemoveTag,
    onResetFilters,
    onChangeStatusFilter,
    onChangeLogType,
    onChangeLogDate,
    onSelectTimezone
}: LogsFilterProps) => {
    const { t } = useTranslation();
    const defaultDateRange = DateRange.find(option => option.label === "Past 4 days");
    const [selectedLogType, setSelectedLogType] = useState<any>(null);
    const [selectedLogLevel, setSelectedLogLevel] = useState<any>(null);
    const [selectedDateRange, setSelectedDateRange] = useState<any>(DateRange.find(option => option.label === "Past 5 days") || null);

    const handleLogTypeChange = (e: any) => {
        setSelectedLogType(e);
        onChangeLogType(e);
    };

    const handleLogLevelChange = (e: any) => {
        setSelectedLogLevel(e);
        onChangeStatusFilter(e);
    };

    const handleDateRangeChange = (e: any) => {
        setSelectedDateRange(e);
        onChangeLogDate(e, "from_date");
    };

    const handleResetFilters = () => {
        setSelectedLogType(null);
        setSelectedLogLevel(null);
        setSelectedDateRange(null);

        onResetFilters();
    };

    useEffect(() => {
        if (defaultDateRange) {
            onChangeLogDate(defaultDateRange, "from_date");
        }
    }, [defaultDateRange, onChangeLogDate]);

    return (
        <div className="w-full justify-between flex flex-row items-start text-off-black bg-very-light-gray dark:bg-off-black dark:text-white mr-3 mb-3 h-auto px-3 pb-3 transition-all duration-300">
            <div className="flex flex-row">
                {/* global */}
                <div>
                    <label className="text-secondary-gray dark:text-gray-lines text-sm">{t("Keywords")}</label>
                    <GlobalSearch
                        onChangeGlobalSearch={onChangeGlobalSearch}
                        savedString={globalSearchStr}
                    />
                    {globalSearchStr && (
                        <div className="flex flex-row border border-gray-lines dark:border-neutral-gray-600 rounded-sm mt-2 w-fit p-1 items-end transition-colors duration-300">
                            <p className="text-secondary-gray dark:text-neutral-gray-300 text-sm pb-1 pt-0.5 px-1 mr-1 transition-colors duration-300">{t("Contains")}: </p>
                            <div className="flex flex-row bg-light-green dark:bg-dark-green-900 pb-1 pt-0.5 px-1 rounded-sm items-end transition-colors duration-300">
                                <p className="text-sm w-fit dark:text-dark-green-100 transition-colors duration-300">
                                    "{globalSearchStr}"

                                </p>
                                <span className="text-dark-green dark:text-dark-green-100 cursor-pointer transition-colors duration-300"
                                    onClick={() => onChangeGlobalSearch("")}>
                                    <Clear />
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* log type */}
                <div className="">
                    <label className="text-secondary-gray dark:text-gray-lines text-sm">{t("Log Type")}</label>
                    <SelectInput
                        options={LogTypes.map(option => ({
                            ...option,
                            label: t(option.label, option.label) // Use direct translation key with fallback
                        }))}
                        data-testid="select_timezone_activity_logs_filter"
                        className={"basic-single dark:text-white pb-1 w-60 text-sm"}
                        defaultValue={undefined}
                        value={selectedLogType ? {
                            ...selectedLogType,
                            label: t(selectedLogType.label, selectedLogType.label)
                        } : null}
                        isDisabled={false}
                        isLoading={false}
                        isClearable={true}
                        isSearchable={true}
                        isMulti={false}
                        name={"Filter by Organization"}
                        onChange={handleLogTypeChange}
                        variant="activityLog"
                        keepOptionsOrder={true}
                    />
                </div>
                {/* status */}
                <div className="mx-2">
                    <label className="text-secondary-gray dark:text-gray-lines text-sm">{t("Minimum Log Level")}</label>
                    <SelectInput
                        options={LogLevel.map(option => ({
                            ...option,
                            label: t(option.label, option.label) // Use direct translation key with fallback
                        }))}
                        data-testid="select_timezone_activity_logs_filter"
                        className={"basic-single dark:text-white pb-1 w-60 text-sm"}
                        defaultValue={undefined}
                        value={selectedLogLevel ? {
                            ...selectedLogLevel,
                            label: t(selectedLogLevel.label, selectedLogLevel.label)
                        } : null}
                        isDisabled={false}
                        isLoading={false}
                        isClearable={true}
                        isSearchable={true}
                        isMulti={false}
                        name={"Filter by Organization"}
                        onChange={handleLogLevelChange}
                        variant="activityLog"
                        keepOptionsOrder={true}
                    />
                </div>

                {/* date & time */}
                <div className="">
                    <label className="text-secondary-gray dark:text-gray-lines text-sm">{t("Date Range")}</label>
                    <SelectInput
                        options={DateRange.map(option => ({
                            ...option,
                            label: t(option.label, option.label) // Use direct translation key with fallback
                        }))}
                        data-testid="select_timezone_activity_logs_filter"
                        className={"basic-single dark:text-white pb-1 w-60 text-sm"}
                        value={selectedDateRange ? {
                            ...selectedDateRange,
                            label: t(selectedDateRange.label, selectedDateRange.label)
                        } : null}
                        isDisabled={false}
                        isLoading={false}
                        isClearable={true}
                        isSearchable={true}
                        isMulti={false}
                        name={"Date Range"}
                        onChange={handleDateRangeChange}
                        variant="activityLog"
                        keepOptionsOrder={true}
                    />

                    {/* {logDateTimeFilterExpanded &&
                        <div>
                            <div className="flex flex-col mt-2">
                                <label
                                    className="inline-block text-sm text-secondary-gray"
                                >
                                    {t("Start")}
                                </label>
                                <DatePicker
                                    selected={filters.map((a: any) => a.name).includes("Start Date & Time") ? new Date(filters.find((a: any) => a.name === "Start Date & Time").id) : null}
                                    onChange={(date: Date) => {
                                        setStartDate(date)
                                        onChangeLogDate(date, "from_date")
                                    }}
                                    showTimeSelect
                                    timeIntervals={15}
                                    className="border border-gray-lines rounded-sm p-1 mt-1 text-secondary-gray text-sm w-60"
                                    dateFormat="yyyy-MM-dd HH:mm"
                                    placeholderText="Select start date"
                                />
                            </div>
                            <div className="flex flex-col mt-2">
                                <label
                                    className="inline-block text-sm text-secondary-gray"
                                >
                                    {t("End")}
                                </label>
                                <DatePicker
                                    selected={filters.map((a: any) => a.name).includes("End Date & Time") ? new Date(filters.find((a: any) => a.name === "End Date & Time").id) : null}
                                    minDate={startDate}
                                    onChange={(date: Date) => {
                                        setEndDate(date)
                                        onChangeLogDate(date, "to_date")
                                    }}
                                    showTimeSelect
                                    timeIntervals={15}
                                    className="border border-gray-lines rounded-sm p-1 mt-1 text-secondary-gray text-sm w-60"
                                    dateFormat="yyyy-MM-dd HH:mm"
                                    placeholderText="Select end date"
                                />
                            </div>
                        </div>
                    } */}
                </div>
            </div>
            <button
                className="w-fit bg-light-green dark:bg-dark-green-900 text-dark-green dark:text-dark-green-100 mt-6 px-3 py-2 rounded text-md font-semibold transition-colors duration-300 hover:bg-light-green-hover dark:hover:bg-dark-green-800"
                onClick={handleResetFilters}
            >
                Clear All Filters
            </button>
        </div>
    )
}

export default LogsFilter;